import { useRef, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  apiErrorText,
  useAddFieldReportWorkMutation,
  useCreateFieldDailyReportMutation,
  useGetFieldElementsQuery,
  useUploadFieldReportPhotoMutation,
} from '@shared/api';
import { parseQtyInput } from '@entities/field';
import { siteShiftDate } from '@entities/field/tituls';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { Combobox } from '@shared/ui/Combobox';

const Box = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 12px;
  margin: 12px 0;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin: 12px 0;
`;
const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
`;
const Select = styled.select`
  padding: 10px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  color: inherit;
`;
const Note = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;
const Error = styled.p.attrs({ role: 'alert' })`
  color: ${({ theme }) => theme.colors.status.danger};
`;

export function CreatePersonalReport({ siteId, timezone }: { siteId: string; timezone: string }) {
  const navigate = useNavigate();
  const [create, { isLoading }] = useCreateFieldDailyReportMutation();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(() => siteShiftDate(timezone));
  const [shift, setShift] = useState('day');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const uuid = useRef(crypto.randomUUID());
  return (
    <Box>
      <Button $size="sm" onClick={() => setOpen((v) => !v)}>
        {open ? 'Скрыть форму' : 'Мой рапорт за смену'}
      </Button>
      {open && (
        <form
          onSubmit={async (e: FormEvent) => {
            e.preventDefault();
            setError('');
            try {
              const result = await create({
                siteId,
                shift_date: date,
                shift_type: shift,
                note: note.trim() || undefined,
                client_uuid: uuid.current,
              }).unwrap();
              uuid.current = crypto.randomUUID();
              navigate(`/agents/field/${siteId}/reports/${result.report.id}`);
            } catch (e) {
              setError(apiErrorText(e, 'Не удалось создать ваш рапорт.'));
            }
          }}
        >
          <Note>
            Один личный рапорт на дату и смену на каждом фронте. Рапорты других прорабов не
            объединяются с вашим.
          </Note>
          <Grid>
            <Label>
              Дата
              <Input
                type="date"
                required
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  uuid.current = crypto.randomUUID();
                }}
              />
            </Label>
            <Label>
              Смена
              <Select
                value={shift}
                onChange={(e) => {
                  setShift(e.target.value);
                  uuid.current = crypto.randomUUID();
                }}
              >
                <option value="day">Дневная</option>
                <option value="night">Ночная</option>
              </Select>
            </Label>
            <Label>
              Примечание
              <Input value={note} onChange={(e) => setNote(e.target.value)} />
            </Label>
          </Grid>
          <Button type="submit" disabled={isLoading || !date}>
            {isLoading ? 'Открываю…' : 'Создать / открыть мой рапорт'}
          </Button>
        </form>
      )}
      {error && <Error>{error}</Error>}
    </Box>
  );
}

export function PersonalReportWorkForm({ siteId, reportId }: { siteId: string; reportId: string }) {
  const elements = useGetFieldElementsQuery(siteId);
  const [add, { isLoading }] = useAddFieldReportWorkMutation();
  const [element, setElement] = useState('');
  const [elementLabel, setElementLabel] = useState('');
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('');
  const [qty, setQty] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const uuid = useRef(crypto.randomUUID());
  const payloadKey = useRef('');
  const options = (elements.currentData?.elements ?? []).map((el) => ({
    id: el.id,
    label: el.code ? `${el.code} — ${el.title}` : el.title,
    sublabel: [el.axes, el.marks].filter(Boolean).join(' · '),
  }));
  return (
    <Box>
      <h3>Добавить выполненную работу</h3>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setError('');
          const value = parseQtyInput(qty);
          if (!value || !element || !name.trim() || !unit.trim()) return;
          const key = JSON.stringify([element, name.trim(), unit.trim(), value, comment.trim()]);
          if (payloadKey.current !== key) {
            payloadKey.current = key;
            uuid.current = crypto.randomUUID();
          }
          try {
            await add({
              reportId,
              element_id: element,
              name: name.trim(),
              unit: unit.trim(),
              qty_fact: String(value),
              comment: comment.trim() || undefined,
              client_uuid: uuid.current,
            }).unwrap();
            uuid.current = crypto.randomUUID();
            setName('');
            setUnit('');
            setQty('');
            setComment('');
            setElement('');
            setElementLabel('');
          } catch (e) {
            setError(apiErrorText(e, 'Не удалось добавить работу.'));
          }
        }}
      >
        <Label>
          Элемент / границы работ
          <Combobox
            value={element}
            selectedLabel={elementLabel}
            options={options}
            loading={elements.isFetching}
            onChange={(o) => {
              setElement(o?.id ?? '');
              setElementLabel(o?.label ?? '');
              if (o && !name) setName(o.label);
            }}
            placeholder="Найдите элемент по шифру или названию"
            maxItems={25}
          />
        </Label>
        <Grid>
          <Label>
            Выполненная работа
            <Input required value={name} onChange={(e) => setName(e.target.value)} />
          </Label>
          <Label>
            Количество
            <Input
              required
              inputMode="decimal"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              placeholder="18,5"
            />
          </Label>
          <Label>
            Единица
            <Input
              required
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              placeholder="м³"
            />
          </Label>
          <Label>
            Комментарий
            <Input value={comment} onChange={(e) => setComment(e.target.value)} />
          </Label>
        </Grid>
        <Button
          type="submit"
          disabled={
            isLoading || !element || !name.trim() || !unit.trim() || parseQtyInput(qty) === null
          }
        >
          Добавить работу
        </Button>
        {elements.isError && (
          <Error>
            {apiErrorText(
              elements.error,
              'Конструктив не загрузился. Попросите инженера подготовить элементы этого фронта.',
            )}
          </Error>
        )}
        {!elements.isFetching && options.length === 0 && (
          <Note>
            Нет конструктива. Инженер сначала назначает РД титулу и добавляет элементы фронта.
          </Note>
        )}
        {error && <Error>{error}</Error>}
      </form>
    </Box>
  );
}

export function PersonalReportPhotoUpload({
  siteId,
  reportId,
}: {
  siteId: string;
  reportId: string;
}) {
  const [upload, { isLoading }] = useUploadFieldReportPhotoMutation();
  const elementsQ = useGetFieldElementsQuery(siteId);
  const [elementId, setElementId] = useState('');
  const [elementName, setElementName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState('progress');
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const uuid = useRef(crypto.randomUUID());
  const fileInput = useRef<HTMLInputElement>(null);
  return (
    <Box>
      <h3>Приложить фотографию к моему рапорту</h3>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!file) return;
          setError('');
          setOk('');
          const body = new FormData();
          body.append('file', file);
          body.append('report_id', reportId);
          if (elementId) body.append('element_id', elementId);
          body.append('source', 'gallery');
          body.append('category', category);
          body.append('client_uuid', uuid.current);
          try {
            await upload({ siteId, body }).unwrap();
            uuid.current = crypto.randomUUID();
            setFile(null);
            if (fileInput.current) fileInput.current.value = '';
            setOk('Фото прикреплено к вашему рапорту.');
          } catch (e) {
            setError(apiErrorText(e, 'Не удалось загрузить фото.'));
          }
        }}
      >
        <Note>
          Загрузка с устройства отмечается как импорт из галереи. Для доказательной съёмки скрытых
          работ с координатами используйте камеру мобильного приложения.
        </Note>
        <Label>
          Работа / элемент на снимке (при необходимости)
          <Combobox
            value={elementId}
            selectedLabel={elementName}
            options={(elementsQ.currentData?.elements ?? []).map((el) => ({
              id: el.id,
              label: el.code ? `${el.code} — ${el.title}` : el.title,
            }))}
            maxItems={25}
            loading={elementsQ.isFetching}
            onChange={(o) => {
              setElementId(o?.id ?? '');
              setElementName(o?.label ?? '');
              uuid.current = crypto.randomUUID();
            }}
            placeholder="К какой работе относится фотография"
          />
        </Label>
        <Grid>
          <Label>
            Фотография
            <Input
              ref={fileInput}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              required
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                uuid.current = crypto.randomUUID();
              }}
            />
          </Label>
          <Label>
            Категория
            <Select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                uuid.current = crypto.randomUUID();
              }}
            >
              <option value="progress">Ход работ</option>
              <option value="defect">Дефект</option>
              <option value="incoming">Входной контроль</option>
              <option value="document">Документ</option>
              <option value="other">Другое</option>
            </Select>
          </Label>
        </Grid>
        <Button type="submit" disabled={isLoading || !file}>
          {isLoading ? 'Загружаю…' : 'Приложить фото'}
        </Button>
        {error && <Error>{error}</Error>}
        {ok && <Note role="status">{ok}</Note>}
      </form>
    </Box>
  );
}
